import React, { useEffect, useState } from 'react';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { IssueRestoreProps } from '../../common/types.tsx';
import { InProgressIssueStoreContainer } from '../../controllers/in-progress-issue/index.tsx';
import {
	IssueRestoreMetaContainer,
	useIssueRestoreMeta,
} from '../../controllers/issue-restore-meta/index.tsx';
import { useInProgressIssueService } from '../../services/in-progress-issue/index.tsx';
import { useIssueRestoreMetaService } from '../../services/issue-restore-meta/index.tsx';
import { InProgressIssueInitialiser } from './in-progress-initialiser/index.tsx';
import { isIndexedDBAvailable } from './utils.tsx';

const IssueRestoreInner = ({ children, ...rest }: IssueRestoreProps) => {
	const [{ inProgressIssueMetas, isInitialised }, { init }] = useIssueRestoreMeta();
	const inProgressIssueService = useInProgressIssueService();
	useEffect(() => {
		if (!isInitialised) {
			init();
		}
	}, [isInitialised, init]);

	// To future proof for bulk issue create
	const activeInProgressIssueID = inProgressIssueMetas?.[0]?.inProgressIssueID;
	const activeShouldRestore = inProgressIssueMetas?.[0]?.shouldRestore;

	if (isInitialised) {
		return (
			<InProgressIssueStoreContainer inProgressIssueService={inProgressIssueService}>
				<InProgressIssueInitialiser
					inProgressIssueID={activeInProgressIssueID}
					shouldRestore={activeShouldRestore}
					{...rest}
				>
					{children}
				</InProgressIssueInitialiser>
			</InProgressIssueStoreContainer>
		);
	}
	return null;
};

export const IssueRestore = ({ children, ...rest }: IssueRestoreProps): React.JSX.Element => {
	const issueRestoreMetaService = useIssueRestoreMetaService();

	const [indexedDBAvailable] = useState(isIndexedDBAvailable());
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (!indexedDBAvailable) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'indexedDB missing');
		}
	}, [createAnalyticsEvent, indexedDBAvailable]);

	if (!indexedDBAvailable) {
		return <>{children?.({})}</>;
	}

	return (
		<IssueRestoreMetaContainer issueRestoreMetaService={issueRestoreMetaService}>
			<IssueRestoreInner {...rest}>{children}</IssueRestoreInner>
		</IssueRestoreMetaContainer>
	);
};
